import React from 'react';
import { Button, Descriptions, Drawer, Space, Tag } from 'antd';
import { formatDate } from '../../utils/dateUtils';
import type { Order } from './types';
import {
  exceptionStatusColors,
  exceptionStatusLabels,
  resolveStatusColor,
  resolveStatusText,
} from './orderStatus';
import SecureOrderImage from './SecureOrderImage';

type Props = {
  detail: Order | null;
  open: boolean;
  productDetailLoadingId: number | null;
  actionLoadingId: number | null;
  onClose: () => void;
  onShowProduct: (productId: number) => void;
  onManualSync: (order: Order) => void;
  onRetry: (order: Order) => void;
  onUpdateRemark: (order: Order) => void;
  onResolveException: (order: Order) => void;
  onReopenException: (order: Order) => void;
};

const OrderDetailDrawer: React.FC<Props> = ({
  detail,
  open,
  productDetailLoadingId,
  actionLoadingId,
  onClose,
  onShowProduct,
  onManualSync,
  onRetry,
  onUpdateRemark,
  onResolveException,
  onReopenException,
}) => (
  <Drawer title={`订单详情 - ${detail?.orderNo || ''}`} placement="right" width={520} onClose={onClose} open={open}>
    {detail && (
      <>
        <Descriptions title="基本信息" bordered size="small" column={1}>
          <Descriptions.Item label="订单号">{detail.orderNo}</Descriptions.Item>
          <Descriptions.Item label="产品ID">
            <Space>
              <span>{detail.productId || '-'}</span>
              <Button type="link" size="small" loading={productDetailLoadingId === detail.productId} onClick={() => onShowProduct(detail.productId)} style={{ padding: 0 }}>
                查看产品
              </Button>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="来源平台">
            <Tag color={detail.sourcePlatform === '172' ? 'orange' : 'blue'}>{detail.sourcePlatform === '172' ? '172平台' : '号易'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="状态"><Tag color={resolveStatusColor(detail)}>{resolveStatusText(detail)}</Tag></Descriptions.Item>
          <Descriptions.Item label="统一状态">{detail.clientStatusText || '-'}</Descriptions.Item>
          <Descriptions.Item label="平台原始状态">{detail.platformStatus || detail.platformStatusText || '-'}</Descriptions.Item>
          <Descriptions.Item label="平台订单号">{detail.platformOrderNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="平台订单ID">{detail.platformOrderId || '-'}</Descriptions.Item>
          <Descriptions.Item label="平台消息">{detail.platformMsg || '-'}</Descriptions.Item>
          <Descriptions.Item label="平台状态原因">{detail.platformReason || detail.platformStatusDetail || '-'}</Descriptions.Item>
          <Descriptions.Item label="激活状态">{detail.activationStatusText || '-'}</Descriptions.Item>
          <Descriptions.Item label="失败原因">{detail.failureReason || detail.platformReason || detail.errorMessage || '-'}</Descriptions.Item>
          <Descriptions.Item label="自动提交次数">{detail.autoSubmitAttempts || 0}</Descriptions.Item>
          <Descriptions.Item label="自动重提次数">{detail.autoRetryCount || 0}</Descriptions.Item>
          <Descriptions.Item label="手动重提次数">{detail.retryCount || 0}</Descriptions.Item>
          <Descriptions.Item label="下次自动重提">{detail.nextAutoRetryAt ? formatDate(detail.nextAutoRetryAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="最近自动重提">{detail.lastAutoRetryAt ? formatDate(detail.lastAutoRetryAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="状态同步时间">{detail.lastStatusSyncAt ? formatDate(detail.lastStatusSyncAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="最近手动同步">{detail.lastManualSyncAt ? formatDate(detail.lastManualSyncAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="最近手动重提">{detail.lastRetryAt ? formatDate(detail.lastRetryAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDate(detail.createdAt)}</Descriptions.Item>
        </Descriptions>

        <Descriptions title="运营处理" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="异常状态">
            {detail.exceptionStatus ? (
              <Tag color={exceptionStatusColors[detail.exceptionStatus] || 'default'}>
                {exceptionStatusLabels[detail.exceptionStatus] || detail.exceptionStatus}
              </Tag>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="异常原因">{detail.exceptionReason || '-'}</Descriptions.Item>
          <Descriptions.Item label="处理人">{detail.exceptionResolvedBy || '-'}</Descriptions.Item>
          <Descriptions.Item label="处理时间">{detail.exceptionResolvedAt ? formatDate(detail.exceptionResolvedAt) : '-'}</Descriptions.Item>
          <Descriptions.Item label="运营备注">{detail.adminRemark || '-'}</Descriptions.Item>
          <Descriptions.Item label="操作">
            <Space wrap>
              <Button loading={actionLoadingId === detail.id} onClick={() => onManualSync(detail)}>手动同步</Button>
              <Button danger={detail.exceptionStatus === 'needs_manual_retry' || detail.status === 'failed'} loading={actionLoadingId === detail.id} onClick={() => onRetry(detail)}>手动重提</Button>
              <Button onClick={() => onUpdateRemark(detail)}>编辑备注</Button>
              {detail.exceptionStatus === 'resolved' ? (
                <Button onClick={() => onReopenException(detail)}>重新打开异常</Button>
              ) : (
                <Button onClick={() => onResolveException(detail)}>标记已处理</Button>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Descriptions title="收件信息" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="姓名">{detail.name}</Descriptions.Item>
          <Descriptions.Item label="手机号">{detail.phone}</Descriptions.Item>
          <Descriptions.Item label="身份证号">{detail.idCard || '-'}</Descriptions.Item>
          <Descriptions.Item label="地址">{detail.province}{detail.city}{detail.district}{detail.address}</Descriptions.Item>
        </Descriptions>

        {(detail.cardNumber || detail.trackingCompany || detail.trackingNo || detail.expressName || detail.expressNo) && (
          <Descriptions title="平台回传" bordered size="small" column={1} style={{ marginTop: 16 }}>
            <Descriptions.Item label="办理号码">{detail.cardNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="物流公司">{detail.trackingCompany || detail.expressName || '-'}</Descriptions.Item>
            <Descriptions.Item label="物流单号">{detail.trackingNo || detail.expressNo || '-'}</Descriptions.Item>
          </Descriptions>
        )}

        {(detail.idCardFront || detail.idCardBack || detail.facePhoto) && (
          <Descriptions title="上传图片" bordered size="small" column={1} style={{ marginTop: 16 }}>
            {detail.idCardFront && <Descriptions.Item label="身份证正面"><SecureOrderImage url={detail.idCardFront} width={120} /></Descriptions.Item>}
            {detail.idCardBack && <Descriptions.Item label="身份证反面"><SecureOrderImage url={detail.idCardBack} width={120} /></Descriptions.Item>}
            {detail.facePhoto && <Descriptions.Item label="人像照"><SecureOrderImage url={detail.facePhoto} width={120} /></Descriptions.Item>}
          </Descriptions>
        )}
      </>
    )}
  </Drawer>
);

export default OrderDetailDrawer;
